import {
  API_BASE_URL,
  ASSETS_URL,
  STATIC_ASSETS_URL,
  isFilesCookieEnabled,
} from "./consts"
import { isBabeleHost } from "./cookies"
import { useAppStore } from "@src/state"

// Uploads live under assets/public or assets/private. Other /assets/ paths are
// files bundled with the web client and load from its own origin.
export const isAssetPath = (path: string) =>
  /^[\\/]?assets[\\/](public|private)[\\/]/.test(path)

const isBundledStaticPath = (path: string) => /^[\\/]?static[\\/]/.test(path)

const isPrivateAssetPath = (path: string) =>
  /^[\\/]?assets[\\/]private[\\/]/.test(path)

// Babele hosts, the custom domain and local builds; other hosts keep their files.
const isOurHost = (host: string) =>
  /(^|\.)babele\.co$/i.test(host) ||
  host.toLowerCase() ===
    useAppStore.getState().customDnsHost?.toLowerCase() ||
  isLocalHost(host)

const SECTION_LOGO =
  /(?:^|[\\/])section-logo-(\d+)(?:-[\w-]{8})?\.(?:png|webp)$/i

// Default program section icons were saved as absolute URLs to the old app,
// /static and /src/static paths, and hashed bundle paths. The static assets
// host serves all of them as /static/section-logos/section-logo-N.png.
export const sectionLogoUrl = (url: string): string | undefined => {
  if (/images[\\/]|assets[\\/](public|private)[\\/]/i.test(url))
    return undefined
  const absolute = url.match(/^https?:\/\/([^/?#]+)/i)
  if (absolute && !isOurHost(absolute[1])) return undefined
  const match = url.match(SECTION_LOGO)
  return match
    ? `${STATIC_ASSETS_URL}/static/section-logos/section-logo-${match[1]}.png`
    : undefined
}

// Host that serves a stored file path. assets/ paths are served by the API
// (the assets hosts only serve legacy Images paths). Off babele hosts (a
// custom domain), the filesToken cookie stays on the current host, so private
// files go through the app's own /assets/private proxy. Files bundled with the
// web client exist only on the static assets host.
export const assetsHostFor = (path: string): string => {
  if (isBundledStaticPath(path)) return STATIC_ASSETS_URL
  if (!isAssetPath(path)) return ASSETS_URL
  if (isFilesCookieEnabled() && !isBabeleHost() && isPrivateAssetPath(path))
    return window.location.origin
  return API_BASE_URL
}

// The editor's attach button saves absolute links, so a local build saved local ones.
export const isLocalHost = (host: string) =>
  /^(localhost|127\.0\.0\.1|\[::1\])(:\d+)?$/i.test(host)

// Absolute URLs saved earlier point at the old app, the custom domain, the
// assets host, the API or a local build. Re-point them at the host that serves their path now.
export const rehostSavedUrl = (url: string): string | undefined => {
  const { customDnsHost } = useAppStore.getState()
  const hosts = ["https://app.babele.co", ASSETS_URL, STATIC_ASSETS_URL]
  if (customDnsHost) hosts.push(`https://${customDnsHost}`)
  const local = url.match(/^http:\/\/([^/?#]+)/i)
  if (local && isLocalHost(local[1])) hosts.push(local[0])

  const host = hosts.find((h) => url.startsWith(h))
  if (!host) return undefined

  const path = url.slice(host.length)
  // Anything else on these hosts is an app page, such as a survey link.
  if (!/^\/(assets|images|static)\//i.test(path)) return undefined
  return `${assetsHostFor(path)}${path}`
}
